import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AlertaContratos } from "@/components/AlertaContratos";
import { AlertaPeriodoPrueba } from "@/components/AlertaPeriodoPrueba";
import { AlertaDocumentos } from "@/components/AlertaDocumentos";
import { RogeMascota, RogeMascotaHeader } from "@/components/RogeMascota";
import { can } from "@/lib/api";
import { MenuUsuario } from "@/components/MenuUsuario";
import { OfertaPassword } from "@/components/OfertaPassword";
import { BannerVisor } from "@/components/VisorEmpresas";
import { BarraMovil } from "@/components/BarraMovil";
import { Contactanos } from "@/components/Contactanos";
import {
  Home, Users, FileSignature, FileText, Upload, Settings, FolderOpen, Files, UserCog, Lock, Award, BookOpen,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Inicio", icon: Home, perm: "ver", end: true },
  { to: "/", label: "Mis empleados", icon: Users, perm: "cliente", end: true },
  { to: "/empleados", label: "Empleados", icon: Users, perm: "ver" },
  { to: "/contrataciones", label: "Contrataciones", corto: "Contratos", icon: FileSignature, perm: "contrataciones" },
  { to: "/certificaciones", label: "Certificaciones", corto: "Certificados", icon: Award, perm: "ver" },
  { to: "/expedientes", label: "Expedientes", icon: FolderOpen, perm: "expedientes" },
  { to: "/plantillas", label: "Plantillas", icon: Files, perm: "plantillas" },
  { to: "/documentos", label: "Documentos", icon: FileText, perm: "ver" },
  { to: "/reglamento", label: "Reglamento Interno de Trabajo", corto: "Reglamento", icon: BookOpen, perm: "reglamento", testid: "nav-reglamento" },
  { to: "/reglamento", label: "Reglamento Interno de Trabajo", corto: "Reglamento", icon: BookOpen, perm: "cliente", testid: "nav-reglamento-cliente" },
  { to: "/importaciones", label: "Importaciones", icon: Upload, perm: "importar" },
  { to: "/usuarios", label: "Usuarios y roles", corto: "Usuarios", icon: UserCog, perm: "usuarios_empleados" },
  { to: "/configuracion", label: "Configuración", icon: Settings, perm: "config" },
];

export default function Layout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    document.body.classList.toggle("modo-visor", user?.role === "visor");
  }, [user]);
  if (user?.role === "visor" && /^\/(usuarios|configuracion|importaciones|plantillas|duplicados|empleados\/duplicados)/.test(pathname)) return <Navigate to="/" replace />;

  const items = NAV.filter((n) => can(user, n.perm));

  const SidebarInner = (
    <div className="flex h-full flex-col bg-vino-900 text-white">
      <div className="flex shrink-0 items-center gap-3 px-5 py-5">
        <div className="flex items-center justify-center rounded-lg bg-white px-2 py-1.5">
          <img src="/human-logo-full.png" alt="Human Resources" className="h-7 w-auto" />
        </div>
      </div>
      <div className="shrink-0"><RogeMascota /></div>
      <nav className="sidebar-scroll min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-2" data-testid="sidebar-nav">
        {items.map((n) => (
          <NavLink
            key={`${n.to}-${n.perm}`}
            to={n.to}
            end={n.end}
            data-testid={n.testid || `nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-vino-800 text-white border-r-4 border-vino-400"
                  : "text-vino-100/80 hover:bg-vino-800/60 hover:text-white"
              }`
            }
          >
            <n.icon className="h-[18px] w-[18px] shrink-0" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      {user.role === "empresa_cliente" && <div className="shrink-0 px-3 pt-2"><Contactanos tipo="cliente" /></div>}
      <div className="shrink-0 px-3 py-4 text-[11px] text-vino-100/50">
        <p>Roge · Etapa 1</p>
        <p>Empleados y matriz Excel</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden w-64 shrink-0 lg:block">{SidebarInner}</aside>

      <BarraMovil items={items} extra={<div className="space-y-2">{user.role === "empresa_cliente" && <Contactanos tipo="cliente" variante="tarjeta" />}<div className="rounded-2xl bg-vino-900 py-2 text-white"><RogeMascota testid="gato-roge-mas" /></div></div>} />

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white/95 px-3 backdrop-blur-md sm:px-6 lg:h-16">
          <div className="flex shrink-0 items-center gap-2 lg:gap-3">
            <RogeMascotaHeader />
            <div className="hidden items-center gap-2 text-sm lg:flex">
              <span className="font-semibold text-vino-800">HUMAN</span>
              <span className="text-slate-300">/</span>
              <span className="text-slate-500">Roge RMSA</span>
            </div>
          </div>
          <div className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-3">
          {["admin", "gestor"].includes(user?.role) && <AlertaDocumentos />}
          {["admin", "gestor"].includes(user?.role) && <AlertaPeriodoPrueba />}
          {["admin", "gestor", "empresa_cliente"].includes(user?.role) && <AlertaContratos puedeFiltrarEmpresa={user.role !== "empresa_cliente"} />}
          <MenuUsuario rol={user.role_label} etiqueta={
            <span className="hidden text-left sm:block">
              <span className="block text-xs font-semibold leading-tight text-slate-800">{user.name}</span>
              <span className="block text-[11px] leading-tight text-slate-400">{user.role_label}</span>
            </span>} />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-0">
          {user.is_active === false && (
            <div data-testid="banner-solo-lectura" className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 sm:px-6 lg:px-8">
              <Lock className="h-4 w-4 shrink-0" />
              Su cuenta está en modo <strong>solo lectura</strong>: puede consultar su información y documentos, pero no modificarlos.
            </div>
          )}
          <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
            <BannerVisor user={user} />
            <Outlet />
          </div>
        </main>
        <OfertaPassword />
      </div>
    </div>
  );
}
